// Novala CountryPack Configuration
// SINGLE SOURCE OF TRUTH for all country-specific values.

export type CountryCode = 'CA' | 'US' | 'GB' | 'AU' | 'NZ' | 'SG' | 'JP' | 'DE' | 'FR' | 'ZA';
export type PlanId = 'essentials' | 'premium' | 'scale';
export type PayrollTierId = 'core' | 'premium' | 'elite';

export interface PlanPricing { regular: number; promo: number; }
export interface PayrollPricing { regular: number; promo: number; perEmployee: number; }

export interface CountryPack {
  code: CountryCode;
  name: string;
  currency: string;
  currencySymbol: string;
  currencyPosition: 'before' | 'after';
  language: string;
  tax: { rate: number; label: string };
  compliance: { dataProtection: string; taxAuthority: string };
  paymentProvider: string;
  prices: {
    plans: Record<PlanId, PlanPricing>;
    payroll: Record<PayrollTierId, PayrollPricing>;
  };
  promo: { active: boolean; savePercent: number; months: number };
}

export const COUNTRIES: Partial<Record<CountryCode, CountryPack>> = {
  CA: {
    code: 'CA',
    name: 'Canada',
    currency: 'CAD',
    currencySymbol: '$',
    currencyPosition: 'before',
    language: 'en-CA',
    tax: { rate: 0.05, label: 'GST' },
    compliance: { dataProtection: 'PIPEDA', taxAuthority: 'CRA' },
    paymentProvider: 'VoPay',
    prices: {
      plans: {
        essentials: { regular: 29, promo: 17 },
        premium: { regular: 69, promo: 41 },
        scale: { regular: 129, promo: 77 },
      },
      payroll: {
        core: { regular: 20, promo: 15, perEmployee: 3 },
        premium: { regular: 35, promo: 25, perEmployee: 4 },
        elite: { regular: 55, promo: 40, perEmployee: 5 },
      },
    },
    promo: { active: true, savePercent: 40, months: 6 },
  },
};

export const DEFAULT_COUNTRY: CountryCode = 'CA';

export function getCountry(code?: CountryCode): CountryPack {
  const c = COUNTRIES[code || DEFAULT_COUNTRY];
  if (!c) {
    const fb = COUNTRIES[DEFAULT_COUNTRY];
    if (!fb) throw new Error('Default country pack missing');
    return fb;
  }
  return c;
}

export function getPlan(planId: PlanId, cc?: CountryCode): PlanPricing {
  return getCountry(cc).prices.plans[planId];
}

export function getPayroll(tierId: PayrollTierId, cc?: CountryCode): PayrollPricing {
  return getCountry(cc).prices.payroll[tierId];
}

export function formatAmount(amount: number, cc?: CountryCode): string {
  const c = getCountry(cc);
  return c.currencyPosition === 'before' ? c.currencySymbol + amount.toFixed(2) : amount.toFixed(2) + c.currencySymbol;
}

export function getTaxRate(cc?: CountryCode): number { return getCountry(cc).tax.rate; }
export function getTaxLabel(cc?: CountryCode): string { const c = getCountry(cc); return c.tax.label + ' (' + Math.round(c.tax.rate * 100) + '%)'; }
export function getPromoMonths(cc?: CountryCode): number { return getCountry(cc).promo.months; }
export function isPromoActive(cc?: CountryCode): boolean { return getCountry(cc).promo.active; }
export function getComplianceLabel(cc?: CountryCode): string { return getCountry(cc).compliance.dataProtection; }
